import { createFileRoute, redirect } from "@tanstack/react-router";
import { pruneSearch } from "@/lib/router-search";
import {
	decodeEntityTypeParam,
	reencodeLiteralEntityTypePath,
} from "@/lib/url";

/** Event-filter keys that only mean something on the Events stream. */
const EVENTS_ONLY_KEYS = [
	"agent",
	"agent_id",
	"automation",
	"produced_by",
	"schedule",
	"connection_ids",
	"feed_ids",
	"run_ids",
	"content_ids",
	"entity_id",
	"run_id",
	"platforms",
	"at",
] as const;

const text = (v: unknown): string | undefined =>
	typeof v === "string" && v.trim() ? v.trim() : undefined;

function hasEventsShape(s: Record<string, unknown>): boolean {
	if (s.view === "events" || s.view === "feeds") return true;
	if (s.browse_view === "events") return true;
	return EVENTS_ONLY_KEYS.some(
		(key) => s[key] != null && String(s[key]).trim() !== "",
	);
}

/**
 * `/memory` was renamed to `/data`, but links to it live on in places nothing
 * can rewrite: the server stored `/{owner}/memory?<run_ids|content_ids|…>`
 * permalinks in notification events (append-only), and bookmarks and chat
 * messages carry the older data forms. Each keeps landing where it meant:
 * an entity detail on its record page, a type scope on its type page, the
 * type map on the Data hub, and every events-shaped link (including the bare
 * activity-feed fallback) on `/events` with its query unchanged.
 */
export const Route = createFileRoute("/$owner/memory/")({
	beforeLoad: ({ params, search }) => {
		const s = search as Record<string, unknown>;
		const owner = params.owner;
		// An entity-detail intent wins over any stray events keys, matching the
		// pre-split precedence.
		const detail = text(s.browse_path) ?? text(s.entity);
		if (detail) {
			throw redirect({
				to: reencodeLiteralEntityTypePath(`/${owner}/${detail}`) as "/",
				search: {},
				replace: true,
			});
		}
		if (hasEventsShape(s)) {
			throw redirect({
				to: "/$owner/events",
				params: { owner },
				search: pruneSearch({
					...s,
					// Legacy `?browse_type` carried the type scope for the old
					// `browse_view=events` form.
					type: text(s.browse_type)
						? decodeEntityTypeParam(text(s.browse_type) as string)
						: (s.type as string | undefined),
					view: undefined,
					create: undefined,
					create_type: undefined,
					browse_type: undefined,
					browse_view: undefined,
				}) as Record<string, string | undefined>,
				replace: true,
			});
		}
		const type = text(s.type) ?? text(s.browse_type);
		if (type) {
			throw redirect({
				to: "/$owner/$entityType",
				params: { owner, entityType: decodeEntityTypeParam(type) },
				search: {
					create: s.create === true || s.create === "true" ? true : undefined,
				},
				replace: true,
			});
		}
		// The type map itself (`?view=entities`, the new-type subpage).
		if (
			s.view === "entities" ||
			s.create_type === true ||
			s.create_type === "true"
		) {
			throw redirect({
				to: "/$owner/data",
				params: { owner },
				search: {
					create_type:
						s.create_type === true || s.create_type === "true"
							? "true"
							: undefined,
				},
				replace: true,
			});
		}
		throw redirect({
			to: "/$owner/events",
			params: { owner },
			search: s as Record<string, string>,
			replace: true,
		});
	},
});
